import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, BookOpen, ChevronRight, Pencil, Plus } from 'lucide-react';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  EcranEnErreur,
  EnteteEcran,
  MessageErreur,
  Pastille,
  type TonPastille,
} from '../components/ui/Primitives';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ModaleProgramme } from '../components/programmes/ModaleProgramme';
import { useAuth } from '../auth/useAuth';
import { ErreurApi } from '../lib/api';
import {
  useAbandonnerRevision,
  useCreerProgramme,
  useModifierProgramme,
  useProgrammes,
  useSupprimerProgramme,
  useValiderProgramme,
  type BlocAffiche,
  type ContenuSaisi,
  type EtatBloc,
  type Programme,
  type VersionProgramme,
} from '../lib/useProgrammes';

const FORMAT_DATE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeZone: 'Indian/Reunion' });

function date(iso: string): string {
  return FORMAT_DATE.format(new Date(iso));
}

const ETATS_BLOC: Record<EtatBloc, { texte: string; ton: TonPastille }> = {
  INCHANGE: { texte: 'Validé', ton: 'success' },
  MODIFIE: { texte: 'Modifié', ton: 'warning' },
  AJOUTE: { texte: 'Ajouté', ton: 'info' },
};

function pastilleProgramme(programme: Programme): { texte: string; ton: TonPastille } {
  if (programme.statut === 'BROUILLON') return { texte: 'Brouillon', ton: 'neutral' };
  if (programme.enEdition) return { texte: 'En révision', ton: 'warning' };
  if (!programme.actif) return { texte: 'Désactivé', ton: 'neutral' };
  return { texte: 'Validé', ton: 'success' };
}

/** Ce que l'en-tête dit de l'état du programme. */
function etatDuProgramme(programme: Programme): string {
  if (programme.statut === 'BROUILLON') {
    return 'Brouillon : il n’est proposé ni aux dossiers ni aux sessions tant qu’il n’est pas validé.';
  }
  const validation = programme.validation
    ? `Validé le ${date(programme.validation.le)}${programme.validation.par ? ` par ${programme.validation.par}` : ''}`
    : 'Validé';
  if (programme.enEdition?.ouverte) {
    const { le, par } = programme.enEdition.ouverte;
    return `Révision ouverte le ${date(le)}${par ? ` par ${par}` : ''} : la version validée reste en usage jusqu’à la validation de la révision.`;
  }
  return programme.actif
    ? `${validation} · proposé aux dossiers, aux sessions et au catalogue.`
    : `${validation} · désactivé dans « Formations & programmes ».`;
}

/**
 * « Ingénierie pédagogique » (TF-472) : le responsable pédagogique et la
 * direction créent un programme en brouillon, le valident, puis le
 * retravaillent dans une révision. Toute règle vient de l'API (`refus*`,
 * état des blocs, changements) : l'écran ne fait que l'afficher.
 */
export function PedIngenieriePage(): JSX.Element {
  const { data, isLoading, error } = useProgrammes();
  const { peutAcceder } = useAuth();
  const toast = useToast();
  const [parametres, setParametres] = useSearchParams();
  const [edition, setEdition] = useState<{ programme: Programme | null } | null>(null);
  const [confirmation, setConfirmation] = useState<'VALIDER' | 'ABANDONNER' | 'SUPPRIMER' | null>(null);

  const creer = useCreerProgramme();
  const modifier = useModifierProgramme();
  const valider = useValiderProgramme();
  const abandonner = useAbandonnerRevision();
  const supprimer = useSupprimerProgramme();
  const occupe = creer.isPending || modifier.isPending || valider.isPending || abandonner.isPending || supprimer.isPending;

  if (error) {
    return (
      <EcranEnErreur
        titre="Ingénierie pédagogique"
        message={error instanceof ErreurApi ? error.message : 'Les programmes n’ont pas pu être chargés.'}
      />
    );
  }
  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-[1500px] p-4 md:p-6">
        <h1 className="sr-only">Ingénierie pédagogique</h1>
        <Chargement libelle="Chargement des programmes…" />
      </div>
    );
  }

  const choisi = data.programmes.find((programme) => programme.id === parametres.get('programme')) ?? data.programmes[0];
  const choisir = (id: string): void => setParametres({ programme: id }, { replace: true });

  const erreurGeste = [valider.error, abandonner.error, supprimer.error].find(
    (candidate): candidate is ErreurApi => candidate instanceof ErreurApi,
  );
  const erreurEdition = [creer.error, modifier.error].find(
    (candidate): candidate is ErreurApi => candidate instanceof ErreurApi,
  );

  const enregistrer = (contenu: ContenuSaisi & { code?: string }): void => {
    const programme = edition?.programme ?? null;
    if (programme === null) {
      creer.mutate(
        { ...contenu, code: contenu.code ?? '' },
        {
          onSuccess: (cree) => {
            setEdition(null);
            choisir(cree.id);
            toast('Brouillon créé · il n’est proposé nulle part avant sa validation');
          },
        },
      );
      return;
    }
    modifier.mutate(
      { id: programme.id, ...contenu },
      {
        onSuccess: () => {
          setEdition(null);
          toast(programme.statut === 'BROUILLON' ? 'Brouillon enregistré' : 'Révision enregistrée · la version validée reste en usage');
        },
      },
    );
  };

  const confirmer = (): void => {
    if (!choisi) return;
    const fermer = { onSuccess: () => setConfirmation(null) };
    if (confirmation === 'VALIDER' && choisi.enEdition) {
      valider.mutate(
        { id: choisi.id, version: choisi.enEdition.version },
        {
          onSuccess: () => {
            setConfirmation(null);
            toast('Programme validé · il sert aux dossiers et aux sessions');
          },
        },
      );
    } else if (confirmation === 'ABANDONNER') {
      abandonner.mutate(choisi.id, fermer);
    } else if (confirmation === 'SUPPRIMER') {
      supprimer.mutate(choisi.id, {
        onSuccess: () => {
          setConfirmation(null);
          setParametres({}, { replace: true });
          toast('Brouillon supprimé');
        },
      });
    }
  };

  const ouvrirEdition = (programme: Programme | null): void => {
    creer.reset();
    modifier.reset();
    setEdition({ programme });
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 md:p-6">
      <EnteteEcran
        titre="Ingénierie pédagogique"
        sousTitre="Construction et révision des programmes · intitulé, certification visée, codes et blocs de compétences"
        actions={
          <BoutonMint onClick={() => ouvrirEdition(null)} disabled={occupe}>
            <span className="inline-flex items-center gap-2">
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouveau programme
            </span>
          </BoutonMint>
        }
      />

      {!choisi ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-4 py-14 text-center shadow-card">
          <BookOpen className="h-9 w-9 text-gray-400" aria-hidden="true" />
          <p className="text-sm font-semibold text-codialis-navy">Aucun programme</p>
          <p className="max-w-md text-xs text-codialis-gray">
            Créez un premier programme : il reste en brouillon jusqu’à sa validation.
          </p>
        </div>
      ) : (
        <>
          {/* Sous `lg`, la liste des programmes tombe en bas de page : on change de programme ici. */}
          <label className="block lg:hidden">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-codialis-navy">
              Programme affiché
            </span>
            <select
              value={choisi.id}
              onChange={(evenement) => choisir(evenement.target.value)}
              className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20"
            >
              {data.programmes.map((programme) => (
                <option key={programme.id} value={programme.id}>
                  {(programme.enEdition ?? programme.valide)!.titre} ({programme.code}) · {pastilleProgramme(programme).texte}
                </option>
              ))}
            </select>
          </label>

          <EnTete
            programme={choisi}
            occupe={occupe}
            onModifier={() => ouvrirEdition(choisi)}
            onValider={() => setConfirmation('VALIDER')}
            onAbandonner={() => setConfirmation('ABANDONNER')}
            onSupprimer={() => setConfirmation('SUPPRIMER')}
          />

          {erreurGeste && <MessageErreur message={erreurGeste.message} />}
          {choisi.enEdition && choisi.refusValidation && (
            <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
              <span>
                <strong className="font-semibold">Validation impossible : </strong>
                {choisi.refusValidation}
              </span>
            </p>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              <CarteBlocs programme={choisi} occupe={occupe} onModifier={() => ouvrirEdition(choisi)} />
              {choisi.enEdition?.revision && <CarteChangements programme={choisi} />}
            </div>
            <div className="min-w-0 space-y-6">
              <CarteCertification
                programme={choisi}
                lienFormations={choisi.statut === 'VALIDE' && peutAcceder('adm-formations')}
              />
              <CarteModalites version={(choisi.enEdition ?? choisi.valide)!} />
              <CarteProgrammes programmes={data.programmes} choisi={choisi.id} onChoisir={choisir} />
            </div>
          </div>
        </>
      )}

      {edition && (
        <ModaleProgramme
          programme={edition.programme}
          niveauxConnus={data.niveaux}
          occupe={occupe}
          erreur={erreurEdition?.message ?? null}
          onFermer={() => setEdition(null)}
          onEnregistrer={enregistrer}
        />
      )}

      {choisi && (
        <Modale
          titre={
            confirmation === 'VALIDER'
              ? `Valider le programme ${choisi.code} ?`
              : confirmation === 'ABANDONNER'
                ? 'Abandonner la révision ?'
                : 'Supprimer ce brouillon ?'
          }
          ouverte={confirmation !== null}
          onFermer={() => setConfirmation(null)}
          largeur="max-w-lg"
        >
          <div className="space-y-4 px-4 py-5 sm:px-6">
            <p className="text-sm leading-relaxed text-codialis-gray">
              {confirmation === 'VALIDER'
                ? choisi.statut === 'BROUILLON'
                  ? 'Le programme devient une formation proposée aux dossiers, aux sessions, au catalogue et aux cours en ligne. Son code est alors figé.'
                  : 'La révision remplace la version en usage : le programme imprimé, la convention, le CERFA et les sessions liront la nouvelle version.'
                : confirmation === 'ABANDONNER'
                  ? 'La révision est effacée. La version validée reste telle quelle.'
                  : `Le brouillon « ${choisi.enEdition?.titre ?? choisi.code} » est définitivement effacé.`}
            </p>
            {erreurGeste && <MessageErreur message={erreurGeste.message} />}
            <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
              <BoutonSecondaire onClick={() => setConfirmation(null)}>Annuler</BoutonSecondaire>
              <BoutonPrincipal onClick={confirmer} disabled={occupe}>
                {confirmation === 'VALIDER'
                  ? 'Valider le programme'
                  : confirmation === 'ABANDONNER'
                    ? 'Abandonner la révision'
                    : 'Supprimer le brouillon'}
              </BoutonPrincipal>
            </div>
          </div>
        </Modale>
      )}
    </div>
  );
}

function EnTete({
  programme,
  occupe,
  onModifier,
  onValider,
  onAbandonner,
  onSupprimer,
}: {
  programme: Programme;
  occupe: boolean;
  onModifier: () => void;
  onValider: () => void;
  onAbandonner: () => void;
  onSupprimer: () => void;
}): JSX.Element {
  const version = (programme.enEdition ?? programme.valide)!;
  const surtitre =
    programme.statut === 'BROUILLON'
      ? 'Programme en brouillon'
      : programme.enEdition
        ? 'Programme en révision'
        : 'Programme validé';
  return (
    <div className="grid grid-cols-1 items-center gap-5 rounded-2xl bg-gradient-to-br from-codialis-navy to-codialis-blue p-4 text-white sm:p-6 lg:grid-cols-4">
      <div className="min-w-0 lg:col-span-3">
        <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-codialis-mint">{surtitre}</div>
        <h2 className="font-display mb-1 text-xl font-bold">
          {version.titre} · RNCP {version.rncp}
        </h2>
        <p className="text-sm text-white/70">{etatDuProgramme(programme)}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {programme.enEdition && (
            <BoutonMint onClick={onValider} disabled={occupe || programme.refusValidation !== null}>
              {programme.statut === 'BROUILLON' ? 'Valider le programme' : 'Valider la révision'}
            </BoutonMint>
          )}
          <button
            type="button"
            onClick={onModifier}
            disabled={occupe}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
            {programme.statut === 'BROUILLON'
              ? 'Modifier le brouillon'
              : programme.enEdition
                ? 'Modifier la révision'
                : 'Réviser le programme'}
          </button>
          {programme.enEdition?.revision && (
            <button
              type="button"
              onClick={onAbandonner}
              disabled={occupe}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-white/80 underline-offset-2 transition hover:text-white hover:underline disabled:opacity-60"
            >
              Abandonner la révision
            </button>
          )}
          {programme.statut === 'BROUILLON' && (
            <button
              type="button"
              onClick={onSupprimer}
              disabled={occupe || programme.refusSuppression !== null}
              title={programme.refusSuppression ?? undefined}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold text-white/80 underline-offset-2 transition hover:text-white hover:underline disabled:opacity-60"
            >
              Supprimer le brouillon
            </button>
          )}
        </div>
      </div>
      <div className="text-center">
        <div className="font-display text-4xl font-extrabold text-codialis-mint">{version.heures} h</div>
        <div className="mt-1 text-xs text-white/60">volume annoncé du programme</div>
      </div>
    </div>
  );
}

function LigneBloc({ bloc, volume, pastille }: { bloc: BlocAffiche; volume: number; pastille: { texte: string; ton: TonPastille } }): JSX.Element {
  const part = volume > 0 ? Math.min(100, Math.round((bloc.heures / volume) * 100)) : 0;
  return (
    <li className="px-4 py-4 sm:px-6">
      <div className="mb-2 flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-codialis-navy">{bloc.libelle}</div>
          <div className="text-[11px] text-codialis-gray">{bloc.heures} h</div>
        </div>
        <Pastille texte={pastille.texte} ton={pastille.ton} />
      </div>
      <div className="flex items-center gap-2">
        <div className="progress-track h-1.5 min-w-0 flex-1 overflow-hidden rounded-full">
          <div className="progress-bar h-full" style={{ width: `${part}%` }} />
        </div>
        <span className="w-28 shrink-0 text-right text-[11px] text-codialis-gray">{part} % du volume</span>
      </div>
    </li>
  );
}

function CarteBlocs({
  programme,
  occupe,
  onModifier,
}: {
  programme: Programme;
  occupe: boolean;
  onModifier: () => void;
}): JSX.Element {
  const version = (programme.enEdition ?? programme.valide)!;
  const pastilleDe = (bloc: BlocAffiche): { texte: string; ton: TonPastille } =>
    bloc.etat ? ETATS_BLOC[bloc.etat] : programme.statut === 'BROUILLON' ? { texte: 'Brouillon', ton: 'neutral' } : ETATS_BLOC.INCHANGE;
  return (
    <Carte
      titre="Blocs de compétences"
      actions={
        <BoutonSecondaire onClick={onModifier} disabled={occupe}>
          Modifier les blocs
        </BoutonSecondaire>
      }
    >
      {version.blocs.length === 0 && (programme.enEdition?.blocsRetires.length ?? 0) === 0 ? (
        <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">Aucun bloc pour l’instant.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {version.blocs.map((bloc, index) => (
            <LigneBloc key={bloc.id ?? `nouveau-${index}`} bloc={bloc} volume={version.heures} pastille={pastilleDe(bloc)} />
          ))}
          {programme.enEdition?.blocsRetires.map((bloc) => (
            <li key={`retire-${bloc.libelle}`} className="flex items-center gap-4 px-4 py-4 sm:px-6">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-codialis-gray line-through">{bloc.libelle}</div>
                <div className="text-[11px] text-codialis-gray">{bloc.heures} h · retiré par la révision</div>
              </div>
              <Pastille texte="Retiré" ton="danger" />
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

function CarteChangements({ programme }: { programme: Programme }): JSX.Element {
  const changements = programme.enEdition?.changements ?? [];
  return (
    <Carte titre="Ce que la révision change">
      {changements.length === 0 ? (
        <p className="px-4 py-6 text-sm text-codialis-gray sm:px-6">Rien pour l’instant : la révision reprend la version validée.</p>
      ) : (
        <ul className="space-y-2 p-4 sm:p-6">
          {changements.map((changement, index) => (
            <li key={`${changement.champ}-${index}`} className="rounded-xl bg-gray-50 px-3 py-2.5 text-sm">
              <div className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">{changement.champ}</div>
              <div className="mt-0.5 text-codialis-navy">
                {changement.avant && <span className="text-codialis-gray line-through">{changement.avant}</span>}
                {changement.avant && changement.apres && (
                  <span aria-hidden="true" className="mx-1.5 text-codialis-gray">
                    ›
                  </span>
                )}
                {changement.avant && changement.apres && <span className="sr-only"> devient </span>}
                {changement.apres && <span className="font-medium">{changement.apres}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <dt className="text-codialis-gray">{libelle}</dt>
      <dd className="text-right font-medium text-codialis-navy">{valeur}</dd>
    </div>
  );
}

function CarteCertification({ programme, lienFormations }: { programme: Programme; lienFormations: boolean }): JSX.Element {
  const version = (programme.enEdition ?? programme.valide)!;
  return (
    <Carte titre="Certification & codes">
      <div className="space-y-3 p-4 sm:p-6">
        <dl className="space-y-3">
          <Ligne libelle="Code" valeur={<span className="font-mono">{programme.code}</span>} />
          <Ligne libelle="Niveau" valeur={version.niveau} />
          <Ligne
            libelle="Niveau BPF"
            valeur={version.niveauBpf ?? <span className="text-amber-700">indéterminé</span>}
          />
          <Ligne libelle="Code diplôme" valeur={<span className="font-mono">{version.codeDiplome}</span>} />
          <Ligne libelle="RNCP" valeur={<span className="font-mono">{version.rncp}</span>} />
          <Ligne
            libelle="Rattachements"
            valeur={`${programme.rattachements.dossiers} dossier(s) · ${programme.rattachements.sessions} session(s)`}
          />
        </dl>
        {lienFormations && (
          <Link
            to={`/adm-formations?formation=${encodeURIComponent(programme.id)}`}
            className="inline-flex items-center gap-1 text-xs font-semibold text-codialis-sky hover:underline"
          >
            Cycle, horaires et calendrier dans « Formations & programmes »
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        )}
      </div>
    </Carte>
  );
}

function CarteModalites({ version }: { version: VersionProgramme }): JSX.Element {
  const blocs = version.blocs.reduce((somme, bloc) => somme + bloc.heures, 0);
  const coherent = version.heuresAuProgramme === version.heures;
  return (
    <Carte titre="Modalités & évaluation">
      <dl className="space-y-3 p-4 sm:p-6">
        <Ligne libelle="Blocs de compétences" valeur={`${blocs} h`} />
        <Ligne libelle="Écrits" valeur={`${version.heuresEcrits} h`} />
        <Ligne libelle="ECF" valeur={`${version.heuresEcf} h`} />
        <Ligne
          libelle="Réparti / annoncé"
          valeur={
            <span className={coherent ? '' : 'text-amber-700'}>
              {version.heuresAuProgramme} h / {version.heures} h
            </span>
          }
        />
      </dl>
    </Carte>
  );
}

function CarteProgrammes({
  programmes,
  choisi,
  onChoisir,
}: {
  programmes: Programme[];
  choisi: string;
  onChoisir: (id: string) => void;
}): JSX.Element {
  return (
    <Carte titre="Programmes">
      <ul className="divide-y divide-gray-100">
        {programmes.map((programme) => {
          const version = (programme.enEdition ?? programme.valide)!;
          const pastille = pastilleProgramme(programme);
          const courant = programme.id === choisi;
          return (
            <li key={programme.id}>
              <button
                type="button"
                onClick={() => onChoisir(programme.id)}
                aria-current={courant ? 'true' : undefined}
                className={`flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-gray-50 sm:px-6 ${
                  courant ? 'bg-codialis-sand' : ''
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-codialis-navy">{version.titre}</span>
                  <span className="block text-[11px] text-codialis-gray">
                    {programme.code} · RNCP {version.rncp} · {version.heures} h
                  </span>
                </span>
                <Pastille texte={pastille.texte} ton={pastille.ton} />
                <ChevronRight className="h-4 w-4 shrink-0 text-codialis-gray" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    </Carte>
  );
}
